import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { generateJob, getClients, createJobManual, createClient, getJobs, getScoringDefaults } from "../api";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { LoadingOverlay } from "../components/LoadingOverlay";
import ClientForm, { BLANK_CLIENT_FORM, clientValuesToFormData, type ClientFormValues } from "../components/ClientForm";
import { SUPPORTED_JOB_LANGUAGES } from "../utils/language";

function LanguageSelector({ language, setLanguage }: { language: string; setLanguage: (v: string) => void }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>Posting Language</label>
      <select value={language} onChange={e => setLanguage(e.target.value)} style={inputStyle}>
        {SUPPORTED_JOB_LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
      </select>
    </div>
  );
}

function ClientSelector({ clientId, setClientId }: { clientId: string; setClientId: (v: string) => void }) {
  const qc = useQueryClient();
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: getClients });
  const [creating, setCreating] = useState(false);
  const [newClient, setNewClient] = useState<ClientFormValues>(BLANK_CLIENT_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function handleCreate(values: ClientFormValues) {
    setSaving(true); setError("");
    try {
      const client = await createClient(clientValuesToFormData(values));
      await qc.invalidateQueries({ queryKey: ["clients"] });
      setClientId(String(client.id));
      setCreating(false);
      setNewClient(BLANK_CLIENT_FORM);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create client.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={labelStyle}>Client</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <select
          value={clientId}
          onChange={e => {
            if (e.target.value === "__new__") { setCreating(true); }
            else { setClientId(e.target.value); setCreating(false); }
          }}
          style={{ ...inputStyle, flex: 1 }}
        >
          <option value="">No specific client</option>
          {(clients as any[]).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
          <option value="__new__">+ Create new client…</option>
        </select>
        {clientId && clientId !== "__new__" && (
          <button type="button" onClick={() => setClientId("")}
            style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12, color: "#64748b" }}>
            ✕
          </button>
        )}
      </div>

      {creating && (
        <div style={{ marginTop: 12, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "16px 16px 12px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e3a8a", marginBottom: 12 }}>New Client</div>
          <ClientForm
            mode="compact"
            value={newClient}
            onChange={setNewClient}
            onSubmit={handleCreate}
            onCancel={() => { setCreating(false); setNewClient(BLANK_CLIENT_FORM); setError(""); }}
            existingClients={clients as any[]}
            submitting={saving}
            submitLabel="Save Client"
            error={error}
            autoFocus
            styles={{
              label: labelStyle,
              input: inputStyle,
              submitButton: { ...btnStyle("#1e3a8a", "white"), fontSize: 13, padding: "7px 16px" },
              cancelButton: { ...btnStyle("#e2e8f0", "#334155"), fontSize: 13, padding: "7px 14px" },
            }}
          />
        </div>
      )}
    </div>
  );
}

export default function NewJobPage() {
  const nav = useNavigate();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [mode, setMode] = useState<"ai" | "manual">("ai");
  const [jobInput, setJobInput] = useState("");
  const [clientId, setClientId] = useState("");
  const [language, setLanguage] = useState("English");
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<any>(null);
  const [showHints, setShowHints] = useState(false);
  const [hintExperience,   setHintExperience]   = useState("");
  const [hintLocation,     setHintLocation]      = useState("");
  const [hintType,         setHintType]          = useState("");
  const [hintBudget,       setHintBudget]        = useState("");
  const [hintNationality,  setHintNationality]   = useState("");
  const [hintLanguage,     setHintLanguage]      = useState("");
  const [hintCvEmail,      setHintCvEmail]       = useState("");

  // Manual form fields
  const [manualTitle, setManualTitle] = useState("");
  const [manualSummary, setManualSummary] = useState("");
  const [manualResponsibilities, setManualResponsibilities] = useState("");
  const [manualSkills, setManualSkills] = useState("");
  const [manualNiceToHave, setManualNiceToHave] = useState("");
  const [manualExperience, setManualExperience] = useState("");
  const [manualLocation, setManualLocation] = useState("");
  const [manualType, setManualType] = useState("Contract");
  const [manualRoleType, setManualRoleType] = useState("");
  const [manualBudget, setManualBudget] = useState("");
  const [knockouts, setKnockouts] = useState<{ type: string; value: string }[]>([]);
  const [aiKnockouts, setAiKnockouts] = useState<{ type: string; value: string }[]>([]);
  const [manualLinkedIn, setManualLinkedIn] = useState("");

  const { data: existingJobs = [] } = useQuery({ queryKey: ["jobs"], queryFn: getJobs });

  // Pre-fill from Recruiter Preferences > Scoring — only once, and only while the
  // recruiter hasn't touched the fields yet. Everything stays overridable per job.
  const { data: scoringDefaults } = useQuery({ queryKey: ["scoring-defaults"], queryFn: getScoringDefaults });
  const prefilled = React.useRef(false);
  React.useEffect(() => {
    if (!scoringDefaults || prefilled.current) return;
    prefilled.current = true;
    if (scoringDefaults.role_type) setManualRoleType(prev => prev || scoringDefaults.role_type);
    if (scoringDefaults.knockouts.length) {
      setKnockouts(prev => prev.length ? prev : scoringDefaults.knockouts.map(k => ({ ...k })));
      setAiKnockouts(prev => prev.length ? prev : scoringDefaults.knockouts.map(k => ({ ...k })));
    }
  }, [scoringDefaults]);

  const mutation = useMutation({
    mutationFn: (fd: FormData) => generateJob(fd),
    onSuccess: (data) => setResult(data),
  });

  const manualMutation = useMutation({
    mutationFn: (fd: FormData) => createJobManual(fd),
    onSuccess: (data) => nav(`/jobs/${data.job_id}`),
  });

  async function checkDuplicate(title: string): Promise<boolean> {
    const norm = title.trim().toLowerCase();
    const dupe = (existingJobs as any[]).find((j: any) =>
      j.title?.trim().toLowerCase() === norm &&
      (clientId ? String(j.client_id) === clientId : true) &&
      j.status !== "closed"
    );
    if (!dupe) return true;
    return confirm(
      `A job titled "${dupe.title}"${dupe.client ? ` for ${dupe.client}` : ""} already exists. Create another anyway?`,
      { confirmLabel: "Create Anyway", cancelLabel: "Cancel", danger: false }
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      const letters = jobInput.replace(/[^a-zA-Z]/g, "");
      if (letters.length < 4 || new Set(letters.toLowerCase()).size < 3) {
        toast("Please enter a real job title or requirements (e.g. 'Senior Python Developer').", "error");
        return;
      }
    }
    const titleGuess = jobInput.split(/[\n,]/)[0].trim();
    if (titleGuess && !(await checkDuplicate(titleGuess))) return;
    const fd = new FormData();
    if (jobInput)         fd.append("job_input",       jobInput);
    if (clientId)         fd.append("client_id",        clientId);
    if (file)             fd.append("file",             file);
    if (hintExperience)   fd.append("experience",       hintExperience);
    if (hintLocation)     fd.append("location",         hintLocation);
    if (hintType)         fd.append("employment_type",  hintType);
    if (hintBudget)       fd.append("budget",           hintBudget);
    if (hintNationality)  fd.append("nationality",      hintNationality);
    if (hintLanguage)     fd.append("language_req",     hintLanguage);
    if (hintCvEmail)      fd.append("cv_email",         hintCvEmail);
    fd.append("language", language.trim() || "English");
    const validAiKnockouts = aiKnockouts.filter(k => k.value.trim());
    if (validAiKnockouts.length) fd.append("knockout_criteria", JSON.stringify(validAiKnockouts));
    mutation.mutate(fd);
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(await checkDuplicate(manualTitle))) return;
    const fd = new FormData();
    fd.append("title", manualTitle);
    fd.append("summary", manualSummary);
    fd.append("responsibilities", manualResponsibilities);
    fd.append("required_skills", manualSkills);
    fd.append("nice_to_have", manualNiceToHave);
    fd.append("experience_years", manualExperience);
    fd.append("location", manualLocation);
    fd.append("employment_type", manualType);
    fd.append("role_type", manualRoleType);
    fd.append("budget", manualBudget);
    fd.append("linkedin_post", manualLinkedIn);
    fd.append("language", language.trim() || "English");
    fd.append("knockout_criteria", JSON.stringify(knockouts.filter(k => k.value.trim())));
    if (clientId) fd.append("client_id", clientId);
    manualMutation.mutate(fd);
  };

  if (result) {
    const jd = result.jd;
    return (
      <div style={{ maxWidth: 800 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 24 }}>
          <button onClick={() => nav("/jobs")} style={btnStyle("#e2e8f0", "#334155")}>← Back</button>
          <h2 style={{ margin: 0, color: "#0f172a" }}>Job Generated: {jd.title}</h2>
        </div>

        <div style={cardStyle}>
          <h3 style={sectionHead}>Job Description</h3>
          <p style={{ color: "#334155", fontSize: 14, lineHeight: 1.6 }}>{jd.summary}</p>

          <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
            <div>
              <strong style={{ color: "#1e3a8a", fontSize: 13 }}>Required Skills</strong>
              <ul style={listStyle}>{jd.required_skills?.map((s: string) => <li key={s}>{s}</li>)}</ul>
            </div>
            <div>
              <strong style={{ color: "#1e3a8a", fontSize: 13 }}>Nice to Have</strong>
              <ul style={listStyle}>{jd.nice_to_have?.map((s: string) => <li key={s}>{s}</li>)}</ul>
            </div>
          </div>

          <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 16 }}>
            <Info label="Experience" value={jd.experience_years} />
            <Info label="Location" value={jd.location} />
            <Info label="Type" value={jd.employment_type} />
          </div>
        </div>

        <div style={cardStyle}>
          <h3 style={sectionHead}>LinkedIn Post Preview</h3>
          <div style={{
            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8,
            padding: 16, fontFamily: "Arial", fontSize: 14, color: "#334155",
            whiteSpace: "pre-wrap", lineHeight: 1.6
          }}>{jd.linkedin_post}</div>
          <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {jd.hashtags?.map((h: string) => (
              <span key={h} style={{
                background: "#eff6ff", color: "#1d4ed8",
                padding: "2px 10px", borderRadius: 20, fontSize: 12
              }}>{h}</span>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
          <button
            onClick={() => nav(`/jobs/${result.job_id}`)}
            style={btnStyle("#1e3a8a", "white")}
          >View Job & Post to LinkedIn →</button>
          <button onClick={() => { setResult(null); setJobInput(""); setFile(null); }}
            style={btnStyle("#e2e8f0", "#334155")}>Generate Another</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 700 }}>
      <LoadingOverlay active={mutation.isPending || manualMutation.isPending} />
      <div style={{ marginBottom: 24 }}>
        <button onClick={() => nav("/jobs")} style={btnStyle("#e2e8f0", "#334155")}>← Back</button>
      </div>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <h1>Create New Job</h1>
          <p>AI-generated or manual job description with instant publish</p>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="tab-pill-bar" style={{ marginBottom: 24 }}>
        {(["ai", "manual"] as const).map(m => (
          <button key={m} onClick={() => setMode(m)} className={mode === m ? "active" : ""}>
            {m === "ai" ? "AI Generate" : "Manual Entry"}
          </button>
        ))}
      </div>

      {mode === "manual" && (
        <form onSubmit={handleManualSubmit} style={cardStyle}>
          <ClientSelector clientId={clientId} setClientId={setClientId} />
          <LanguageSelector language={language} setLanguage={setLanguage} />
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Job Title *</label>
            <input required value={manualTitle} onChange={e => setManualTitle(e.target.value)} style={inputStyle} placeholder="e.g. Senior Java Developer" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Summary</label>
            <textarea value={manualSummary} onChange={e => setManualSummary(e.target.value)} rows={3} style={{ ...inputStyle, resize: "vertical" }} placeholder="Brief role overview..." />
          </div>
          <div className="grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Required Skills (one per line)</label>
              <textarea value={manualSkills} onChange={e => setManualSkills(e.target.value)} rows={4} style={{ ...inputStyle, resize: "vertical" }} placeholder={"Java\nSpring Boot\nPostgreSQL"} />
            </div>
            <div>
              <label style={labelStyle}>Responsibilities (one per line)</label>
              <textarea value={manualResponsibilities} onChange={e => setManualResponsibilities(e.target.value)} rows={4} style={{ ...inputStyle, resize: "vertical" }} placeholder={"Lead backend development\nCode reviews\nSystem design"} />
            </div>
          </div>
          <div className="grid-3col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Experience</label>
              <input value={manualExperience} onChange={e => setManualExperience(e.target.value)} style={inputStyle} placeholder="e.g. 5-7 years" />
            </div>
            <div>
              <label style={labelStyle}>Budget</label>
              <input value={manualBudget} onChange={e => setManualBudget(e.target.value)} style={inputStyle} placeholder="e.g. 120,000 THB/month" />
            </div>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={manualType} onChange={e => setManualType(e.target.value)} style={inputStyle}>
                <option>Contract</option>
                <option>Full-time</option>
                <option>Part-time</option>
              </select>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div>
              <label style={labelStyle}>Role Type <span style={{ color: "#94a3b8", fontWeight: 400 }}>(sets CV scoring weights)</span></label>
              <select value={manualRoleType} onChange={e => setManualRoleType(e.target.value)} style={inputStyle}>
                <option value="">Auto (based on seniority)</option>
                <option value="Technical">Technical — skills &amp; depth weighted</option>
                <option value="Management">Management — leadership &amp; trajectory weighted</option>
                <option value="Sales/BD">Sales / BD — profile &amp; achievements weighted</option>
                <option value="Creative">Creative — portfolio &amp; domain weighted</option>
                <option value="Operations">Operations — process &amp; experience weighted</option>
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>
              Knockout Criteria <span style={{ color: "#94a3b8", fontWeight: 400 }}>(auto-reject if not met — no AI cost)</span>
            </label>
            {knockouts.map((k, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                <select value={k.type} onChange={e => setKnockouts(ks => ks.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}
                  style={{ ...inputStyle, width: 180, marginBottom: 0 }}>
                  <option value="min_experience">Min experience</option>
                  <option value="required_degree">Required degree</option>
                  <option value="location">Must be in location</option>
                </select>
                <input value={k.value} placeholder={k.type === "min_experience" ? "e.g. 5 years" : k.type === "required_degree" ? "e.g. Bachelor" : "e.g. Bangkok"}
                  onChange={e => setKnockouts(ks => ks.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                  style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
                <button type="button" onClick={() => setKnockouts(ks => ks.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 18, padding: "0 4px" }}>×</button>
              </div>
            ))}
            <button type="button" onClick={() => setKnockouts(ks => [...ks, { type: "min_experience", value: "" }])}
              style={{ fontSize: 13, color: "#1e3a8a", background: "none", border: "1px dashed #94a3b8", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}>
              + Add criterion
            </button>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Location</label>
            <input value={manualLocation} onChange={e => setManualLocation(e.target.value)} style={inputStyle} placeholder="e.g. Bangkok, Hybrid 2-3 days/week" />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={labelStyle}>LinkedIn Post Text</label>
            <textarea value={manualLinkedIn} onChange={e => setManualLinkedIn(e.target.value)} rows={6} style={{ ...inputStyle, resize: "vertical" }} placeholder="Write your LinkedIn post here..." />
          </div>
          <button type="submit" disabled={manualMutation.isPending || !manualTitle}
            style={{ ...btnStyle("#1e3a8a", "white"), width: "100%", padding: "12px 0", opacity: manualMutation.isPending || !manualTitle ? 0.6 : 1 }}>
            {manualMutation.isPending ? "Saving..." : "Save Job"}
          </button>
          {manualMutation.isError && <div style={{ color: "#dc2626", fontSize: 13, marginTop: 10 }}>Failed to save. Please try again.</div>}
        </form>
      )}

      {mode === "ai" && <p style={{ color: "#64748b", fontSize: 14, marginBottom: 24 }}>
        Enter a job title, paste client requirements, or upload a PDF — AI will create the full JD and LinkedIn post.
      </p>}

      {mode === "ai" && <form onSubmit={handleSubmit} style={cardStyle}>
        <ClientSelector clientId={clientId} setClientId={setClientId} />
        <LanguageSelector language={language} setLanguage={setLanguage} />

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Job Title or Requirements</label>
          <textarea
            value={jobInput}
            onChange={e => setJobInput(e.target.value)}
            placeholder="e.g. 'Java Developer' or paste full client requirements..."
            rows={6}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Or Upload Client PDF / Word Document</label>
          <input
            type="file"
            accept=".pdf,.docx,.txt"
            onChange={e => setFile(e.target.files?.[0] || null)}
            style={{ fontSize: 13, color: "#334155" }}
          />
          {file && <div style={{ fontSize: 12, color: "#16a34a", marginTop: 4 }}>✓ {file.name}</div>}
        </div>

        {/* Optional hints */}
        <div style={{ marginBottom: 20 }}>
          <button type="button" onClick={() => setShowHints(v => !v)} style={{
            background: "none", border: "none", cursor: "pointer", padding: 0,
            fontSize: 13, fontWeight: 600, color: "#1e3a8a", display: "flex", alignItems: "center", gap: 6,
          }}>
            {showHints ? "▼" : "▶"} Optional Details <span style={{ fontWeight: 400, color: "#94a3b8" }}>(helps AI generate a better JD)</span>
          </button>

          {showHints && (
            <div style={{ marginTop: 14, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px 18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Experience Required</label>
                  <input value={hintExperience} onChange={e => setHintExperience(e.target.value)}
                    style={inputStyle} placeholder="e.g. 3-5 years, 5+ years" />
                </div>
                <div>
                  <label style={labelStyle}>Location</label>
                  <input value={hintLocation} onChange={e => setHintLocation(e.target.value)}
                    style={inputStyle} placeholder="e.g. Bangkok, Remote, Hybrid" />
                </div>
                <div>
                  <label style={labelStyle}>Employment Type</label>
                  <select value={hintType} onChange={e => setHintType(e.target.value)} style={inputStyle}>
                    <option value="">— not specified —</option>
                    <option>Full-time</option>
                    <option>Contract</option>
                    <option>Part-time</option>
                    <option>Freelance</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Salary / Budget</label>
                  <input value={hintBudget} onChange={e => setHintBudget(e.target.value)}
                    style={inputStyle} placeholder="e.g. 80,000–120,000 THB/month" />
                </div>
                <div>
                  <label style={labelStyle}>Nationality / Work Permit</label>
                  <select value={hintNationality} onChange={e => setHintNationality(e.target.value)} style={inputStyle}>
                    <option value="">— not specified —</option>
                    <option>Open to all nationalities</option>
                    <option>Thai nationals only</option>
                    <option>Work permit provided for right candidate</option>
                    <option>Must already hold Thai work permit</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Language Requirements</label>
                  <input value={hintLanguage} onChange={e => setHintLanguage(e.target.value)}
                    style={inputStyle} placeholder="e.g. Fluent English, Thai preferred" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>CV Receiving Email</label>
                <input type="email" value={hintCvEmail} onChange={e => setHintCvEmail(e.target.value)}
                  style={inputStyle} placeholder="e.g. jobs@client.com (overrides company default)" />
              </div>
              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>
                  Knockout Criteria <span style={{ color: "#94a3b8", fontWeight: 400 }}>(auto-reject if not met — no AI cost)</span>
                </label>
                {aiKnockouts.map((k, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                    <select value={k.type} onChange={e => setAiKnockouts(ks => ks.map((x, j) => j === i ? { ...x, type: e.target.value } : x))}
                      style={{ ...inputStyle, width: 180, marginBottom: 0 }}>
                      <option value="min_experience">Min experience</option>
                      <option value="required_degree">Required degree</option>
                      <option value="location">Must be in location</option>
                    </select>
                    <input value={k.value}
                      placeholder={k.type === "min_experience" ? "e.g. 5 years" : k.type === "required_degree" ? "e.g. Bachelor" : "e.g. Bangkok"}
                      onChange={e => setAiKnockouts(ks => ks.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                      style={{ ...inputStyle, flex: 1, marginBottom: 0 }} />
                    <button type="button" onClick={() => setAiKnockouts(ks => ks.filter((_, j) => j !== i))}
                      style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontSize: 18, padding: "0 4px" }}>×</button>
                  </div>
                ))}
                <button type="button" onClick={() => setAiKnockouts(ks => [...ks, { type: "min_experience", value: "" }])}
                  style={{ fontSize: 13, color: "#1e3a8a", background: "none", border: "1px dashed #94a3b8", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}>
                  + Add criterion
                </button>
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={mutation.isPending || (!jobInput && !file)}
          style={{
            ...btnStyle(mutation.isPending ? "#1e3a8a" : "#1e3a8a", "white"),
            opacity: !jobInput && !file ? 0.5 : 1,
            width: "100%", padding: "13px 0",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            cursor: mutation.isPending || (!jobInput && !file) ? "not-allowed" : "pointer",
          }}
        >
          {mutation.isPending ? (
            <>
              <span style={{
                width: 16, height: 16, border: "2px solid rgba(255,255,255,0.3)",
                borderTop: "2px solid white", borderRadius: "50%",
                display: "inline-block", animation: "spin 0.8s linear infinite",
              }} />
              Generating with AI…
            </>
          ) : "✨ Generate Job Description"}
        </button>

        {mutation.isPending && (
          <p style={{ textAlign: "center", fontSize: 12, color: "#64748b", marginTop: 10 }}>
            AI is writing your job description — this usually takes 15–30 seconds.
          </p>
        )}

        {mutation.isError && (
          <div style={{ color: "#dc2626", fontSize: 13, marginTop: 12 }}>
            Error generating JD. Please try again.
          </div>
        )}
      </form>}
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  background: "white", border: "1px solid #e2e8f0", borderRadius: 10,
  padding: 24, marginBottom: 16, boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
};
const sectionHead: React.CSSProperties = {
  color: "#1e3a8a", fontSize: 15, margin: "0 0 12px", borderBottom: "2px solid #00c9a7",
  paddingBottom: 6
};
const listStyle: React.CSSProperties = {
  margin: "6px 0", paddingLeft: 16, fontSize: 13, color: "#334155", lineHeight: 1.8
};
const labelStyle: React.CSSProperties = {
  display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6
};
const inputStyle: React.CSSProperties = {
  width: "100%", padding: "8px 12px", borderRadius: 6,
  border: "1px solid #d1d5db", fontSize: 14, color: "#334155",
  outline: "none", boxSizing: "border-box"
};
function btnStyle(bg: string, color: string): React.CSSProperties {
  return {
    background: bg, color, border: "none", padding: "8px 18px",
    borderRadius: 7, cursor: "pointer", fontWeight: 600, fontSize: 14
  };
}
function Info({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ background: "#f8fafc", borderRadius: 8, padding: "10px 14px" }}>
      <div style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>{value || "—"}</div>
    </div>
  );
}
